import React, { useContext, useMemo } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { ST } from '_lib/api/apiTypes.shim';

import { ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { TextField } from '_lib/components/TextField/TextField';
import { Typography } from '_lib/components/Typography/Typography';
import { FieldIssueProps } from '_lib/utils/validationErrorUtils';

export interface CancelReasonSelectProps extends FieldIssueProps {
  companyId: string;
  onSetCancelReason: (cancelReason: ST.JobCancelReason) => void;
  cancelReason?: ST.JobCancelReason;
}

export const CancelReasonSelect = (props: CancelReasonSelectProps) => {
  const { companyId, onSetCancelReason, cancelReason, issuePath, issues } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(companyId), [api, companyId]);
  const jobCancelReasonsQuery = serviceTitanApi.jobCancelReasons.useUnpagedListCached();

  if (jobCancelReasonsQuery.error) {
    return (
      <ManualErrorBoundary
        errorState={{ message: 'Failed to load Cancel Reasons' }}
        onRefresh={async () => {
          await jobCancelReasonsQuery.refetch();
        }}
      />
    );
  }

  return (
    <TextField
      fullWidth
      label='Job Cancel Reason'
      required
      disabled={jobCancelReasonsQuery.data?.length === 1}
      value={cancelReason?.id || ''}
      defaultValue={''}
      select
      error={{
        issues,
        issuePath
      }}
      onChange={(e) => {
        const cancelReasonId = parseInt(e.target.value);
        if (!isNaN(cancelReasonId)) {
          const selectedCancelReason = jobCancelReasonsQuery.data?.find((c) => c.id === cancelReasonId);
          if (selectedCancelReason) onSetCancelReason(selectedCancelReason);
        }
      }}>
      {jobCancelReasonsQuery.data &&
        jobCancelReasonsQuery.data.length > 0 &&
        jobCancelReasonsQuery.data.map(
          (cancelReason) =>
            cancelReason.active && (
              <MenuItem key={cancelReason.id} value={cancelReason.id}>
                <Typography text={cancelReason.name} />
              </MenuItem>
            )
        )}
      {(!jobCancelReasonsQuery.data || jobCancelReasonsQuery.data?.length === 0) && (
        <MenuItem disabled>No Job Cancel Reasons found.</MenuItem>
      )}
    </TextField>
  );
};
